<template>
  <transition
    name="slide-left"
  >
    <div
      v-if="isMiniOpen"
      class="survey"
    >
      <div
        class="body-2"
        v-text="$t('survey.text')"
      />

      <basic-text-button
        class="survey__close"
        @click="closeMini"
      >
        <template #trailing>
          <the-icon
            art="solid"
            name="xmark"
          />
        </template>
      </basic-text-button>

      <basic-text-button
        :label="$t('survey.btn')"
        @click="openSurvey"
      >
        <template #trailing>
          <the-icon
            art="solid"
            name="arrow-right"
          />
        </template>
      </basic-text-button>
    </div>
  </transition>

  <basic-modal.container
    id="survey"
    ref="modalRef"
  >
    <basic-modal.step
      :id="surveyData.TP"
      size="maxi"
      type="raw"
    >
      <basic-modal.content>
        <iframe
          ref="surveyFrameRef"
          style="border: none"
          width="100%"
          height="500px"
          :src="target"
        />
      </basic-modal.content>

      <basic-modal.footer />
    </basic-modal.step>
  </basic-modal.container>
</template>

<script setup>
  import { computed, nextTick, onBeforeMount, ref } from 'vue'
  import { useRoute } from 'vue-router'

  import { BasicModal, BasicTextButton } from '@shared/components/Basic'

  import { CUSTOM_EVENT_SURVEY_OPEN, CUSTOM_EVENT_SURVEY_TRIGGER } from '@/config/events'

  // COMPOSABLES
  const route = useRoute()

  // DATA
  const modalRef = ref()
  const surveyFrameRef = ref()
  const isMiniOpen = ref(false)
  const surveyData = ref({
    Action: 'START_SURVEY',
    SurveyName: 'CUSTOMER_SATISFACTION',
    L: null,
    TP: null,
    BID: null,
    Vepa: null,
    UUID: null,
  })

  // COMPUTED
  const language = computed(() => route.params.lang)

  const target = computed(() => {
    const url = new URL(`${import.meta.env.VITE_SANITAS_PORTAL_PATH}/${language.value}/shared/survey`)
    const params = Object.fromEntries(
      Object.entries(surveyData.value).filter(([, value]) => value !== null && value !== undefined)
    )
    url.search = new URLSearchParams(params)

    return url
  })

  // METHODS
  function closeMini() {
    isMiniOpen.value = false
  }

  async function openSurvey() {
    closeMini()
    
    // Wait for next tick to ensure the modal component is properly mounted
    await nextTick()
    
    // Check if modalRef is available before calling methods
    if (modalRef.value) {
      modalRef.value.open()

      window.setTimeout(() => {
        window.addEventListener('message', event => {
          if (event.data.type === 'surveyFinished') {
            // Check if modalRef is still available before closing
            if (modalRef.value) {
              modalRef.value.close()
            }
          }
        })
      }, 501)
    } else {
      console.warn('Modal ref is not available. Cannot open survey modal.')
    }
  }

  // LIFECYCLE HOOKS
  onBeforeMount(() => {
    window.addEventListener(CUSTOM_EVENT_SURVEY_OPEN, event => {
      const payload = event.detail
      
      surveyData.value.L = language.value?.toUpperCase()
      surveyData.value.TP = payload.touchPoint
      surveyData.value.BID = payload.leadCode
      surveyData.value.Vepa = payload.vepa ?? null
      surveyData.value.UUID = payload.leadgiver ?? null

      openSurvey()
    })

    window.addEventListener(CUSTOM_EVENT_SURVEY_TRIGGER, event => {
      const payload = event.detail
      surveyData.value.L = language.value?.toUpperCase()
      surveyData.value.TP = payload.touchPoint
      surveyData.value.BID = payload.leadCode
      surveyData.value.Vepa = payload.vepa ?? null
      surveyData.value.UUID = payload.leadgiver ?? null

      isMiniOpen.value = true
    })
  })
</script>

<style scoped>
  .survey {
    position: fixed;

    background-color: var(--on-primary);
    box-shadow: var(--elevation-level-3);
    top: 20%;
    right: 8px;

    height: 175px;
    width: 280px;
    padding: var(--fixed-spacing-fix-06);
    z-index: var(--dvp-stack-level-screen-blocker);
    display: flex;
    flex-direction: column;
    gap: 8px;
    border-radius: var(--radius-border-radius-02);

    .survey__close {
      position: absolute;
      right: 16px;
      top: 16px;
    }

    .survey__frame {
      width: 100%;
    }
  }
</style>

<style name="animations" scoped>
  .slide-left-enter-active,
  .slide-left-leave-active {
    overflow: hidden;
    transition: transform var(--duration-long-1) var(--ease-in-out-cubic);
  }

  .slide-left-enter-from {
    transform: translate(100%, 0);
  }
  .slide-left-leave-to {
    transform: translate(100%, 0);
  }
</style>
